import React from "react";
import { Link, useLocation } from "react-router-dom";

import RoleTabs from "./RoleTabs";
import Accordion, { QA } from "./Accordion";
import ProductShowcase from "./ProductShowcase";
import Reveal from "../redesign/Reveal";

// Hero animation as video (webm/mp4) instead of a 735KB GIF. The lightweight
// poster is the LCP element and is preloaded from index.html.
const HERO = {
  poster: `${process.env.PUBLIC_URL}/hero-poster.webp`,
  webm: `${process.env.PUBLIC_URL}/hero-animation.webm`,
  mp4: `${process.env.PUBLIC_URL}/hero-animation.mp4`,
};
import fear1 from "../../assets/landing/fear-1-buyer.webp";
import fear2 from "../../assets/landing/fear-2-seller.webp";
import fear3 from "../../assets/landing/fear-3-both.webp";
import sol1 from "../../assets/landing/solution-step-1.webp";
import sol2 from "../../assets/landing/solution-step-2.webp";
import sol3 from "../../assets/landing/solution-step-3.webp";
import phone from "../../assets/landing/phone-in-hand.webp";
import aisha from "../../assets/landing/testimonial-aisha.webp";
import emeka from "../../assets/landing/testimonial-emeka.webp";
import tobi from "../../assets/landing/testimonial-tobi.webp";
import motif from "../../assets/landing/motif.png";
import motifTrusted from "../../assets/landing/motif-trusted.svg";
import motifMoveTrust from "../../assets/landing/motif-move-trust.svg";

const STATS = [
  { value: "1500+", label: "protected transactions" },
  { value: "₦150m", label: "processed" },
  { value: "99.2%", label: "success rate" },
  { value: "<24h", label: "dispute resolution" },
];

const FEARS = [
  {
    img: fear1,
    tone: "peach",
    title: "Buyer fears scam after payment",
    body: "Buyer pays upfront, then the vendor goes silent. No product, no refund, no useful trail.",
  },
  {
    img: fear2,
    tone: "yellow",
    title: "Seller fears scam after delivery",
    body: "Seller delivers the goods or service, then gets dragged into excuses and unpaid balances.",
  },
  {
    img: fear3,
    tone: "mint",
    title: "Buyer and seller fear making first move",
    body: "Buyer will not pay first. Seller will not ship first. The deal dies before it starts.",
  },
];

const SOLUTION = [
  {
    img: sol1,
    title: "Buyer pays VendStash",
    body: "The payment lands in escrow. The seller sees it is funded, but cannot touch it yet.",
  },
  {
    img: sol2,
    title: "Seller delivers to buyer",
    body: "Ship the product or complete the service. Delivery is verified in the app.",
  },
  {
    img: sol3,
    title: "VendStash pays the seller",
    body: "On buyer confirmation, funds release instantly. Everybody is happy.",
  },
];

const SECURITY = [
  {
    title: "Held with a CBN-licensed partner",
    body: "Your money sits in a segregated custody account at a trusted Nigerian bank, not on VendStash’s balance sheet.",
  },
  {
    title: "We legally cannot spend it",
    body: "Escrow funds are ring-fenced by law. If VendStash vanished tomorrow, your money would still be recoverable at the bank.",
  },
  {
    title: "Every user is KYC-verified",
    body: "Valid ID and phone verification is carried out on both sides of every deal. 256-bit encryption throughout. Payouts dual-approved.",
  },
  {
    title: "A real dispute team, round the clock",
    body: "Humans review evidence from both sides and resolve cases in under a day on average. Not a bot. Not a form.",
  },
];

const TESTIMONIALS = [
  {
    img: aisha,
    quote:
      "I’ve done over ₦2M in sales on VendStash. Not a single kobo lost. They have saved me so much stress with online sales.",
    name: "Aisha Adeyemo",
    role: "CEO, Bubu Stitches",
  },
  {
    img: emeka,
    quote: "I can now buy high ticket items from Instagram vendors with peace of mind.",
    name: "Emeka Nwachukwu",
    role: "VendStash buyer",
  },
  {
    img: tobi,
    quote: "We route all marketplace payouts through VendStash. ‘I didn’t get it’ claims went to zero.",
    name: "Tobi Adebayo",
    role: "Marketplace operator",
  },
];

const OBJECTIONS: QA[] = [
  {
    question: "What if the seller sends the wrong thing?",
    answer:
      "You have a 48-hour inspection window. Report the issue in-app, upload evidence, and we pause the release and open a dispute automatically.",
  },
  {
    question: "What if the buyer refuses to confirm?",
    answer:
      "If the buyer goes silent after delivery is verified, we auto-release funds after the protection window. You are not left hanging.",
  },
  {
    question: "Is my money safe with VendStash itself?",
    answer:
      "Funds are held in a segregated custody account at a CBN-licensed Nigerian bank. We cannot use escrow funds for anything else.",
  },
  {
    question: "How much does VendStash cost?",
    answer: "A flat escrow fee per transaction with no monthly fees, signup fees, or hidden charges.",
  },
  {
    question: "Do both parties need an account?",
    answer:
      "Yes. Both buyer and seller need VendStash accounts so identity, payment, and dispute records stay attached to the deal.",
  },
];

const FAQ: QA[] = [
  {
    question: "Is VendStash safe?",
    answer:
      "Yes. VendStash uses secure escrow to protect buyers and vendors. Funds remain held until the agreed conditions are met and delivery is confirmed.",
  },
  {
    question: "How do I file a dispute?",
    answer:
      "Open the transaction in the mediation centre, explain what went wrong, and upload your evidence. The release is paused while our support team reviews the case.",
  },
  {
    question: "Can I use VendStash for personal transactions?",
    answer:
      "Yes. VendStash supports both personal and business transactions for individuals, merchants, and vendors.",
  },
  {
    question: "Are there transaction fees?",
    answer:
      "Yes. Fees are transparent and calculated for each transaction, including the relevant payment-channel charges.",
  },
  {
    question: "Do I need a storefront to sell with VendStash?",
    answer:
      "No. You can create a secure payment link and share it directly with a buyer on Instagram, WhatsApp, or anywhere else you sell.",
  },
];

const TrustStats: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <section className={`trust-stats${compact ? " trust-stats--compact" : ""}`}>
    <h2>Trusted by buyers, vendors and businesses</h2>
    <div className='stats-grid' aria-label='VendStash transaction statistics'>
      {STATS.map((s) => (
        <div className='stat' key={s.label}>
          <strong>{s.value}</strong>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  </section>
);

const VendstashLanding: React.FC = () => {
  // /for-buyers and /for-vendors pre-select the matching tab in the roles
  // section; every other path defaults to "buyer".
  const { pathname } = useLocation();
  const initialRole = pathname === "/for-vendors" ? "vendor" : "buyer";

  return (
    <>
      {/* Hero */}
        <section className='hero' id='top'>
          <div className='hero__copy'>
            <h1>
              <span className='hero__line-mask'>
                <span className='hero__line hero__line--one'>Never lose a sale,</span>
              </span>
              <span className='hero__line-mask'>
                <span className='hero__line hero__line--two'>Never lose your money</span>
              </span>
            </h1>
            <p className='hero__description'>
              You buy, sell and complete transactions,
              <br />
              We hold your payments safely
              <br />
              Until everyone fulfills their part of the deal.
            </p>
            <div className='hero__actions'>
              <Link className='button button--navy' to='/signin'>
                Use VendStash
              </Link>
              <a className='action-link' href='#how-it-works'>
                see how it works
              </a>
            </div>
          </div>
          <div className='hero__visual'>
            <video
              className='hero__animation'
              width={960}
              height={768}
              autoPlay
              muted
              loop
              playsInline
              poster={HERO.poster}
              aria-label='A VendStash payment moving safely from buyer to vendor'>
              <source src={HERO.webm} type='video/webm' />
              <source src={HERO.mp4} type='video/mp4' />
            </video>
          </div>
        </section>

        <TrustStats />

        {/* Fears */}
        <section className='fear-section'>
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>Why VendStash</p>
            <h2>Everyone fears online transactions</h2>
          </div>
          <div className='fear-grid'>
            {FEARS.map((f, i) => (
              <Reveal as='article' className='fear-card' delay={i * 90} key={f.title}>
                <div className='fear-card__artwork'>
                  <img
                    alt=''
                    src={f.img}
                    width={297}
                    height={127}
                    loading='lazy'
                    decoding='async'
                  />
                </div>
                <div className={`fear-card__copy fear-card__copy--${f.tone}`}>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Before callout */}
        <section className='before-callout'>
          <p className='eyebrow eyebrow--coral'>Before VendStash</p>
          <h2>Without a solution, sales and money are lost!</h2>
        </section>

        {/* Solution */}
        <section className='solution-section' id='how-it-works'>
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>With VendStash</p>
            <h2>The Solution is VendStash</h2>
            <p className='section-header__description'>
              VendStash is the trusted middle point between buyers and sellers. It works seamlessly
              in 3 simple steps.
            </p>
          </div>
          <div className='solution-grid'>
            {SOLUTION.map((s, i) => (
              <Reveal as='article' className='solution-card' delay={i * 90} key={s.title}>
                <span className='solution-card__number'>{i + 1}</span>
                <img
                  alt=''
                  className='solution-card__artwork'
                  src={s.img}
                  width={209}
                  height={70}
                  loading='lazy'
                  decoding='async'
                />
                <div className='solution-card__copy'>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
                <Link className='action-link' to='/signin'>
                  Get started
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Transformation */}
        <section className='transformation-section'>
          <h2>
            With VendStash, buyers stop fearing scams
            <br />
            And vendors stop chasing payment
          </h2>
          <p>
            Everyone’s money stays protected until the deal is done. No ghosting, no failed
            deliveries, no begging for the balance. VendStash is peace of mind for everyone.
          </p>
          <div className='transformation-section__actions'>
            <Link className='button button--navy' to='/signin'>
              Use VendStash
            </Link>
            <a className='action-link' href='#how-it-works'>
              or see how it works
            </a>
          </div>
          <Reveal className='transformation-section__visual' direction='left'>
            <img
              alt='A VendStash order protected in escrow until delivery confirmation'
              src={phone}
              width={682}
              height={1041}
              loading='lazy'
              decoding='async'
            />
          </Reveal>
        </section>

        {/* Security */}
        <section className='security-section' id='security'>
          <img alt='' aria-hidden='true' className='security-section__motif' src={motif} />
          <div className='section-header section-header--start section-header--light'>
            <p className='eyebrow'>Your money, safe</p>
            <h2>How we keep your money safe</h2>
            <p className='section-header__description'>
              How we protect your money, and why we cannot disappear with it.
            </p>
          </div>
          <div className='security-grid'>
            {SECURITY.map((s, i) => (
              <Reveal as='article' className='security-card' delay={i * 80} key={s.title}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Roles */}
        <section className='role-section' id='roles'>
          <img alt='' aria-hidden='true' className='role-section__motif' src={motif} />
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>Built for both sides</p>
            <h2>Choose how you use VendStash</h2>
            <p className='section-header__description'>
              Buyers need protection before they pay. Sellers need proof of funds before they
              deliver. VendStash keeps the transaction fair for both.
            </p>
          </div>
          <RoleTabs initialRole={initialRole} />
        </section>

        {/* Objections accordion */}
        <section className='accordion-section'>
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>Objections, handled</p>
            <h2>But what if…</h2>
            <p className='section-header__description'>
              The doubts are fair. Here is how VendStash handles the questions buyers and vendors ask
              before trusting a deal.
            </p>
          </div>
          <Accordion items={OBJECTIONS} />
        </section>

        {/* Testimonials */}
        <section className='testimonials-section'>
          <img
            alt=''
            aria-hidden='true'
            className='testimonials-section__motif'
            src={motifTrusted}
          />
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>Trusted by businesses</p>
            <h2>We’re powering the future of online commerce</h2>
            <p className='section-header__description'>
              VendStash is making it possible to buy and sell online with complete confidence.
            </p>
          </div>
          <div className='testimonial-grid'>
            {TESTIMONIALS.map((t, i) => (
              <Reveal as='figure' className='testimonial-card' delay={i * 90} key={t.name}>
                <blockquote>“{t.quote}”</blockquote>
                <figcaption>
                  <img
                    alt=''
                    className='testimonial-card__portrait'
                    src={t.img}
                    width={44}
                    height={44}
                    loading='lazy'
                    decoding='async'
                  />
                  <span>
                    <strong>{t.name}</strong>
                    <small>{t.role}</small>
                  </span>
                </figcaption>
              </Reveal>
            ))}
          </div>
        </section>

        <ProductShowcase />

        <TrustStats compact />

        {/* Philosophy */}
        <section className='philosophy-section'>
          <p className='eyebrow'>Our philosophy</p>
          <h2>Others move money, we move trust.</h2>
          <p>
            Every product decision, from how we hold funds to how we resolve disputes, starts with
            one question: “does this protect every person and every party?”
            <br />
            We win only when both sides win.
          </p>
          <img
            alt=''
            aria-hidden='true'
            className='philosophy-section__motif'
            src={motifMoveTrust}
          />
        </section>

        {/* FAQ accordion */}
        <section className='accordion-section accordion-section--faq' id='faq'>
          <div className='section-header section-header--start section-header--default'>
            <p className='eyebrow'>More answers</p>
            <h2>Frequently asked questions</h2>
            <p className='section-header__description'>
              Everything buyers and vendors usually want to know before trusting their first deal to
              VendStash.
            </p>
          </div>
          <Accordion items={FAQ} />
        </section>

        {/* Final CTA */}
        <section className='final-cta' id='get-started'>
          <div>
            <h2>Your next online deal doesn’t have to be a gamble.</h2>
            <p>Get started in just 2 minutes</p>
          </div>
          <Link className='button button--lime' to='/signin'>
            Use VendStash
          </Link>
        </section>
    </>
  );
};

export default VendstashLanding;
